import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { IoHome, IoHomeOutline, IoSearch, IoSearchOutline, IoGrid, IoGridOutline, IoFlag, IoFlagOutline, IoPerson, IoPersonOutline, IoNotificationsOutline, IoArrowBack, IoLogoYoutube } from 'react-icons/io5';
import { useAuth } from '../contexts/AuthContext';
import { subscribeTeamAssignments } from '../service/youtubeTossService';

const TABS = [
  { key: '/', label: '홈', icon: IoHomeOutline, activeIcon: IoHome },
  { key: '/?menu=youtubeListings', label: '유튜브 매물', icon: IoLogoYoutube, activeIcon: IoLogoYoutube, menuQuery: 'youtubeListings' },
  { key: '/utils', label: '유틸', icon: IoGridOutline, activeIcon: IoGrid },
  { key: '/quest', label: '퀘스트', icon: IoFlagOutline, activeIcon: IoFlag },
  { key: '/my', label: '마이', icon: IoPersonOutline, activeIcon: IoPerson },
];

const SUB_PAGES = {
  '/notices': '공지사항',
  '/chat': '업무 공유',
};

export default function AppLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const subPageTitle = SUB_PAGES[currentPath];
  const { user } = useAuth();
  const isLeaderPlus = user?.dept === '팀장' || user?.dept === '대표' || user?.isAdmin || user?.loginId === 'admin';

  // 미확인 토스 카운트 — 헤더 아이콘 뱃지용
  // 미팅 등록(달력) 아이콘: 본인 배정인데 memberConfirmed 안 된 토스
  // 유튜브방(유튜브 로고) 아이콘: 팀에 들어왔는데 leaderConfirmed 안 된 토스 (팀장+)
  const [leaderUnconfirmed, setLeaderUnconfirmed] = useState(0);
  useEffect(() => {
    if (!isLeaderPlus) { setLeaderUnconfirmed(0); return; }
    const teamId = (user?.store_idx && user?.team_no) ? `${user.store_idx}_${user.team_no}` : '';
    if (!teamId) { setLeaderUnconfirmed(0); return; }
    const unsub = subscribeTeamAssignments(teamId, (list) => {
      setLeaderUnconfirmed(list.filter(a => !a.leaderConfirmedAt).length);
    });
    return () => unsub && unsub();
  }, [isLeaderPlus, user?.store_idx, user?.team_no]);

  const currentMenu = new URLSearchParams(location.search).get('menu') || '';
  const isActive = (tab) => {
    if (tab.menuQuery) return currentPath === '/' && currentMenu === tab.menuQuery;
    if (tab.key === '/') return currentPath === '/' && !currentMenu;
    return currentPath.startsWith(tab.key);
  };

  return (
    <Container>
      {/* 서브 페이지 헤더 (뒤로가기) */}
      {subPageTitle ? (
        <Header>
          <BackBtn onClick={() => navigate(-1)}><IoArrowBack size={22} /></BackBtn>
          <HeaderTitle>{subPageTitle}</HeaderTitle>
          <HeaderSpacer />
        </Header>
      ) : (
        <Header>
          <Logo onClick={() => navigate('/')}>
            <LogoImg src="/favicon.png" alt="풀잎 부동산" style={{ width: 36, height: 36, objectFit: 'contain' }} />
            <LogoText>풀잎부동산</LogoText>
          </Logo>
          <HeaderSpacer />
        </Header>
      )}

      <Content>
        {children}
      </Content>

      {!subPageTitle && <Footer>
        {TABS.map((tab) => {
          const active = isActive(tab);
          const Icon = active ? tab.activeIcon : tab.icon;
          return (
            <TabBtn key={tab.key} $active={active} onClick={() => navigate(tab.key)}>
              <Icon size={22} />
              <TabLabel $active={active}>{tab.label}</TabLabel>
            </TabBtn>
          );
        })}
      </Footer>}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 52px;
  padding: 0 16px;
  background: #000000;
  border-bottom: 1px solid #111111;
  flex-shrink: 0;
  z-index: 100;
  @media (min-width: 769px) {
    display: none;
  }
  /* 패드(아이패드 등)는 폭이 768이어도 PC식 레이아웃 → 모바일 헤더 숨김 */
  html.pad-mode & {
    display: none;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

const LogoImg = styled.img`
  width: 28px;
  height: 28px;
`;

const LogoText = styled.span`
  font-size: 17px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.5px;
`;

const BackBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  background: transparent;
  color: #fff;
`;

const HeaderTitle = styled.span`
  font-size: 17px;
  font-weight: 700;
  color: #fff;
`;

const HeaderSpacer = styled.div`
  width: 38px;
`;

const Content = styled.main`
  flex: 1;
  overflow: hidden;
  position: relative;
`;

const Footer = styled.nav`
  display: flex;
  align-items: center;
  justify-content: space-around;
  height: 56px;
  background: #fff;
  border-top: 1px solid #E5E7EB;
  flex-shrink: 0;
  z-index: 100;
  padding-bottom: env(safe-area-inset-bottom, 0);
  @media (min-width: 769px) {
    display: none;
  }
  /* 패드(아이패드 등)는 하단 메뉴 대신 지도+사이드바 메뉴 사용 → 하단탭 숨김 */
  html.pad-mode & {
    display: none;
  }
`;

const TabBtn = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  background: transparent;
  padding: 4px 12px;
  color: ${({ $active }) => $active ? '#1E3A5F' : '#9CA3AF'};
  transition: color 0.15s;
`;

const TabLabel = styled.span`
  font-size: 11px;
  font-weight: ${({ $active }) => $active ? 600 : 400};
`;
